import ethereum from 'decentralized/ethereum.js';
import EthereumFieldSearcher from 'search/EthereumFieldSearcher.js';
import SearchQuery from 'search/SearchQuery.js';
import LinkedPromiseList from 'search/LinkedPromiseList.js';
import ChordSearcher from 'decentralized/chords/ChordSearcher.js';
import {TAG} from 'constants/field-types.js';
import {YOUTUBE} from 'constants/field-types.js';
import {STYLE} from 'constants/field-types.js';
import {COUNTRY} from 'constants/field-types.js';
import {YEAR} from 'constants/field-types.js';
import {OR} from 'search/constants.js';
import {AND} from 'search/constants.js';
import SearchCache from 'search/SearchCache.js';
import {intersectSounds} from './intersectSounds.js';
import {getSoundsWithFields} from './getSoundsWithFields.js';

const cachedSearches = new SearchCache();
var tagsCache = {};

class EthereumSoundsSearcher extends EthereumFieldSearcher {
    constructor(cache, sequencerEngine) {
        super(cache);
        this.store = sequencerEngine ? sequencerEngine.store : undefined;
        this.cache = cache;
        this.updateSoundRankings();
        cachedSearches.setStore(this.store);
    }

    async getNewIndex(guildId) {
        await this.cache.loadNewIndex(guildId);
    }

    clearCache() {
        cachedSearches.clear();
        tagsCache = {};
    }

    async filterByMonth(date) {
        return await this.cache.filterByMonth(date.getMonth(), date.getFullYear());
    }

    clearFilterByMonth() {
        this.cache.clearFilter();
    }

    get sortBy() {
        return cachedSearches.sortBy;
    }

    set sortBy(x) {
        cachedSearches.sortBy = x;
    }

    // returns "all the sounds", aka the default setting when we load
    // the sound selector pane.
    async getAllSounds(start, size) {
        let query = cachedSearches.getSearchQueryIfExists(this, true);
        if (query !== undefined) {
            return query;
        }
        query = cachedSearches.createSearchQuery(this, true);
        
        let videosSounds = await this.getSoundsWithVideos(this.selectedVideos);
        let discogsSounds = await this.getSoundsWithDiscogs(this.selectedDiscogs);
        query.videosSounds = union(videosSounds, discogsSounds);
        
        let array = [];
        if (this.filterFavorites) {
            array = await ethereum.getFavoritesForUser();
        } else {
            let sounds = undefined;
            if (videosSounds !== undefined || discogsSounds !== undefined) {
                sounds = query.videosSounds;
            } else {
                let toDate = new Date(this.fromDate);
                toDate.setDate(this.fromDate.getDate() + 31);
                let a = new Date().getTime();
                sounds = await ethereum.getAllSounds(this.fromDate, toDate);
                let b = new Date().getTime();
                let time = b - a;
            }
            
            sounds.forEach(s => {
                if (array.indexOf(s) == -1) {
                    array.push(s);
                }
            });
        }
        
        query.setFinalResults(array);
        cachedSearches.addNewQuery(query);
        return query;
    }
    
    
   async getSounds(start, size) {
       // first check the cache of recent searches, whether we've
       // already ran this search
       let query = cachedSearches.getSearchQueryIfExists(this);
       if (query !== undefined) {
           // it was in the cache, so we just return this
           return query;
       }
       query = cachedSearches.createSearchQuery(this);
       
       // search by each filter individually
       // TODO: parallelize
       let dateA = new Date().getTime();
       let tagsSounds = await this.getSoundsWithTags(this.selectedTags);
       let videosSounds = await this.getSoundsWithVideos(this.selectedVideos);
       let discogsSounds = await this.getSoundsWithDiscogs(this.selectedDiscogs);
       let orTagsSounds = await this.getSoundsWithOrTags(
           this.sortTagsByPopularity(this.selectedOrTags));
       let stylesSounds = await this.getSoundsWithStyles(this.selectedStyles);
       let countriesSounds = await this.getSoundsWithCountries(this.selectedCountries);
       let yearSounds = await this.getSoundsWithYear(this.year);
       let chordSounds = await this.getSoundsWithChord(this.chord);
       let dateB = new Date().getTime();
       
       // we need to intersect each list below to generate the search results
       let resultsForFilters = [
           chordSounds, tagsSounds,
           stylesSounds, countriesSounds, yearSounds];
       let sounds = this.intersectResultsForFilters(resultsForFilters);

       // we might need to filter by favorites
       let favorites = this.filterFavorites ? await ethereum.getFavoritesForUser() :
           undefined;
       
       sounds = [... sounds, ... orTagsSounds];
       query.videosSounds = union(videosSounds, discogsSounds);
       query.favorites = favorites;
       query.countriesSounds = countriesSounds;
       query.stylesSounds = stylesSounds;
       query.yearSounds = yearSounds;
       query.chordSounds = chordSounds;
       query.addUnsortedResults(sounds);
       // query.addLazyResults(orTagsSounds);
       cachedSearches.addNewQuery(query);
       
       return query;
   }
    
    async getSoundsWithTags(tags) {
        if (tags.length === 0) {
            return [];
        }
        return new Set(await getSoundsWithFields(tags, (tag) => this.getSoundsWithTag(tag)));
    }

    async getSoundsWithTag(tag) {
        if (tag in tagsCache) {
            return tagsCache[tag];
        }
        let sounds = await ethereum.getSoundsWithTag(tag);
        tagsCache[tag] = sounds;
        return sounds;
    }

    async getSoundsWithVideos(videos) {
        if (videos.length === 0) {
            return undefined;
        }

        let promises = [];
        for (let i in videos) {
            promises.push(ethereum.getSoundsWithVideo(videos[i]));
        }
        let results = await Promise.all(promises);
        let sounds = [];
        for (let i in results) {
            sounds = sounds.concat(results[i]);
        }

        return new Set(sounds);
    }

    async getSoundsWithDiscogs(videos) {
        if (videos.length === 0) {
            return undefined;
        }

        let promises = [];
        for (let i in videos) {
            promises.push(ethereum.getSoundsWithDiscogs(videos[i]));
        }
        let results = await Promise.all(promises);
        let sounds = [];
        for (let i in results) {
            sounds = sounds.concat(results[i]);
        }

        console.log(sounds);

        return new Set(sounds);
    }


    // returns a LinkedPromiseList, i.e. a linked list of promises that
    // fetches sounds by tag one at a time
    async getSoundsWithOrTags(tags) {
        console.log('get sounds with or tags = ', tags);
        if (tags.length === 0) {
            return [];
        }

        return await ethereum.getSoundsWithOrTags(tags);
    }

    async recursivelySearchOrTags(tags) {
        // get next tag
        if (tags.length == 0) {
            return new Promise((resolve, reject) => resolve(undefined));
        }

        const tag = tags[0];

        // recursively construct this linked list, going one tag at a time
        return new LinkedPromiseList(
            () => ethereum.getSoundsWithTag(tag),
            this.recursivelySearchOrTags(tags.slice(1)));
    }

    async getSoundsWithStyles(styles) {
        if (styles.length === 0) {
            return undefined;
        }

        return new Set(await getSoundsWithFields(
            styles,
            (style) =>
                ethereum.getSoundsWithStyle(style)));
    }

    async getSoundsWithCountries(countries) {
        if (countries.length === 0) {
            return undefined;
        }

        return new Set(await getSoundsWithFields(
            countries,
            (country) =>
                ethereum.getSoundsWithCountry(country)));
    }

    async getSoundsWithYear(year) {
        if (year === undefined) {
            return undefined;
        }
        return new Set(
            await ethereum.getSoundsWithYear(this.year));
    }

    async getSoundsWithChord(chord) {
        if (chord === undefined) {
            return undefined;
        }

        return new Set(Object.keys(await new ChordSearcher().searchRankedNotes(this.chord)));
    }

    intersectResultsForFilters(resultsForFilters) {
        if (this.selectedOrTags.length > 0) {
            return [];
        }

        let choseFirst = false;
        let sounds = new Set([]);
        for (let i in resultsForFilters) {
            let soundsWithFields = resultsForFilters[i];
            if (soundsWithFields === undefined) {
                continue;
            }
            if (sounds.size > 0) {
                if (soundsWithFields.size > 0) {
                    sounds = intersectSounds(sounds, soundsWithFields);
                    choseFirst = true;
                } else {
                    continue;
                }
            } else if (!choseFirst && soundsWithFields.size > 0) {
                sounds = soundsWithFields;
            }
        }

        return Array.from(sounds);
    }

    sortTagsByPopularity(tags) {
        let mostPopular = this.cache.getAllForField(TAG, 0, 2000);
        tags.sort((a, b) => mostPopular.indexOf(a) - mostPopular.indexOf(b));
        return tags;
    }

    async updateSoundRankings() {
        this.sampleToRanking = await ethereum.getOverallVotedRankings();
    }
}

function union(a, b) {
    if (a === undefined && b  === undefined) {
        return undefined;
    }
    let s = new Set();
    if (a) {
        a.forEach(x => s.add(x));
    }
    if (b) {
        b.forEach(x => s.add(x));
    }
    console.log('union gave me = ');
    console.log(a);
    console.log(b);
    console.log(s);
    return s;
}


export default EthereumSoundsSearcher;
